import { StandardPhrase } from '@app/graphql-types/Source'
import dayjs from 'dayjs'
import twttrExtractUrlsWithIndices from 'twitter-text/dist/extractUrlsWithIndices'

export const capitalizeFirstLetter = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const checkForPhrases = (
  text: string,
  phrases: StandardPhrase[],
  patient: any,
  providerProfile: any,
) => {
  const match = phrases.find((phrase: any) =>
    text.match(new RegExp(`${phrase.shortName} `.replace('.', '\\.'), 'g')),
  )
  if (match && match.fullPhrase) {
    const replaceText = replaceVariables(
      match.fullPhrase,
      patient,
      providerProfile,
    )
    // when replacing text on Android and it's not at the end of the input the
    // cursor is off by the shortName.length + 1 for the space after it. I
    // didn't want to spend too much time figuring this out since I'm not sure
    // this is a common use case and we may not decide to support Android.
    return text.replace(
      new RegExp(`${match.shortName}`.replace('.', '\\.'), 'g'),
      replaceText,
    )
  }
  return text
}

export const containsOnlyEmoji = (text: string): boolean => {
  const hasEmoji = /\p{Extended_Pictographic}/gmu.test(text)
  const hasOtherCharacters = /[\w.,/#!$%^&*;:{}=\-_`~()?[\]]+/gm.test(text)
  return hasEmoji && !hasOtherCharacters
}

const linkify = (link: string): string => {
  let newLink = link
  newLink = newLink.replace('http:', 'https:')
  if (link.indexOf('http') !== 0) {
    newLink = `https://${link}`
  }
  return `<a href="${newLink}" data-bypass="true" rel="noopener noreferrer" target="_blank">${link}</a>`
}
interface TwttrUrlMatch {
  url: string
  indices: [number, number]
}

export const textWithLinks = (text: string): string => {
  const links = twttrExtractUrlsWithIndices(text) as TwttrUrlMatch[]

  if (links.length === 0) {
    return text
  }

  let hrefOffset = 0 // Indices in `links` need an offset when adding hrefs.
  return links.reduce((linkyText: string, link: TwttrUrlMatch) => {
    const {
      url,
      indices: [start, end],
    } = link

    const urlHref = linkify(url)

    const textWithHref =
      linkyText.substring(0, start + hrefOffset) +
      urlHref +
      linkyText.substring(end + hrefOffset)

    // Track length differences since `links` indices don't take linkify
    // replacements into account.
    hrefOffset += urlHref.length - url.length

    return textWithHref
  }, `${text}`)
}

export const copyToClipboard = (value: string) => {
  const activeElement: any = document.activeElement
  const textarea = document.createElement('textarea')
  textarea.style.cssText = 'position: absolute; left: -99999em'
  // prevent iOS keyboard from popping up
  textarea.setAttribute('readonly', 'true')
  document.body.appendChild(textarea)
  textarea.value = value
  textarea.focus()
  textarea.select()
  try {
    document.execCommand('copy')
  } catch (e) {
    // console.log('copy error', e)
  }
  document.body.removeChild(textarea)
  activeElement.focus()
}

export const uuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export const titleize = (str: string) =>
  str
    .toLowerCase()
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

// titlize only if variable contains 'name' and the value is all caps
export const titleizeNameFromCaps = (variable: string, str: string) =>
  /name/gi.test(variable) && str === str.toUpperCase() ? titleize(str) : str

export const replaceVariable = (variable, text, model, prop) => {
  if (model && model[prop]) {
    const re = new RegExp(variable.replace('$', '\\$'), 'g')
    return text.replace(re, titleizeNameFromCaps(variable, model[prop]))
  }
  return text
}

export const replaceVariableFromFunction = (variable, text, func) => {
  if (func) {
    const re = new RegExp(variable.replace('$', '\\$'), 'g')
    const newText = func()
    if (newText === text) {
      return newText
    }
    return text.replace(re, titleizeNameFromCaps(variable, func()))
  }
  return text
}

export const replaceVariables = (text, patient, providerProfile) => {
  let replaceText = text
  if (/\$[A-Z|_]+/.test(replaceText)) {
    // provider
    replaceText = replaceVariable(
      '$MY_FIRST_NAME',
      replaceText,
      providerProfile,
      'firstName',
    )
    replaceText = replaceVariable(
      '$PROVIDER_FIRST_NAME',
      replaceText,
      providerProfile,
      'firstName',
    )
    replaceText = replaceVariable(
      '$MY_LAST_NAME',
      replaceText,
      providerProfile,
      'lastName',
    )
    replaceText = replaceVariable(
      '$PROVIDER_LAST_NAME',
      replaceText,
      providerProfile,
      'lastName',
    )

    // patient
    replaceText = replaceVariableFromFunction(
      '$PATIENT_FIRST_NAME',
      replaceText,
      () => {
        if (!patient) {
          return replaceText
        }
        if (patient['displayFirstName']) {
          return patient['displayFirstName']
        } else if (patient['firstName']) {
          return patient['firstName']
        } else {
          return replaceText
        }
      },
    )

    replaceText = replaceVariable(
      '$PATIENT_LAST_NAME',
      replaceText,
      patient,
      'lastName',
    )

    replaceText = replaceVariable('$PATIENT_AGE', replaceText, patient, 'age')

    replaceText = replaceVariableFromFunction(
      '$PATIENT_LEGAL_NAME',
      replaceText,
      () => {
        if (!patient) {
          return replaceText
        }
        if (!patient['firstName'] && !patient['lastName']) {
          return replaceText
        }
        return `${patient['firstName']} ${patient['lastName']}`
      },
    )
  }

  return replaceText
}

export const ellipsisText = (t: string, maxLength: number) =>
  t && t.length > maxLength ? `${t.substr(0, maxLength)}...` : t

export const snakeToCamel = (str: string) =>
  str.toLowerCase().replace(/([-_][a-z])/g, (g) => g.slice(-1).toUpperCase())

export const ageFromDob = (dob: string | null): number =>
  Math.floor(dayjs.duration(dayjs().diff(dob || '')).asYears())
